function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: 'Europe/Paris',
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

export default class AdminUsersPanel {
  constructor({ api, root, currentUserId }) {
    this.api = api;
    this.root = root;
    this.currentUserId = currentUserId;
    this.users = [];
    this.guestStats = {};
    this.filter = '';
    this.accountFilter = '';
    this.busyId = null;

    this.root.addEventListener('input', (e) => {
      if (e.target.matches('[data-user-search]')) {
        this.filter = e.target.value;
        this._renderTable();
      }
    });

    this.root.addEventListener('change', (e) => {
      if (e.target.matches('[data-account-filter]')) {
        this.accountFilter = e.target.value;
        this._renderTable();
      }
    });

    this.root.addEventListener('click', (e) => {
      const block = e.target.closest('[data-block-user]');
      if (block) {
        const userId = Number(block.dataset.blockUser);
        const blocked = block.dataset.blocked === 'true';
        this._toggleBlock(userId, blocked);
      }
      const pass = e.target.closest('[data-premium-user]');
      if (pass) {
        const userId = Number(pass.dataset.premiumUser);
        const premium = pass.dataset.premium === 'true';
        this._togglePremium(userId, premium);
      }
    });
  }

  async load() {
    this.root.innerHTML = '<p class="admin-msg">Chargement…</p>';
    const res = await this.api.get('/admin/users');
    if (!res.ok) {
      this.root.innerHTML = '<p class="admin-msg admin-msg-error">Impossible de charger les utilisateurs.</p>';
      return;
    }
    this.users = res.data.users ?? [];
    this.guestStats = res.data.guestStats ?? {};
    this._render();
  }

  async _toggleBlock(userId, blocked) {
    this.busyId = userId;
    this._renderTable();
    const res = await this.api.post('/admin/block', { userId, blocked });
    this.busyId = null;
    if (res.ok) {
      const u = this.users.find((x) => x.id === userId);
      if (u) u.isBlocked = blocked;
    }
    this._renderTable();
  }

  async _togglePremium(userId, premium) {
    this.busyId = userId;
    this._renderTable();
    const res = await this.api.post('/admin/premium', { userId, premium });
    this.busyId = null;
    if (res.ok) {
      const u = this.users.find((x) => x.id === userId);
      if (u) u.isPremium = premium;
    }
    this._renderTable();
  }

  _filteredUsers() {
    const q = this.filter.trim().toLowerCase();
    let list = this.users;
    if (this.accountFilter === 'guest') list = list.filter((u) => u.isGuest);
    if (this.accountFilter === 'registered') list = list.filter((u) => !u.isGuest);
    if (!q) return list;
    return list.filter(
      (u) => u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q),
    );
  }

  _renderTable() {
    const tbody = this.root.querySelector('[data-users-tbody]');
    const empty = this.root.querySelector('[data-users-empty]');
    if (!tbody) return;

    const filtered = this._filteredUsers();
    if (empty) empty.classList.toggle('hidden', filtered.length > 0);

    tbody.innerHTML = filtered.map((user) => {
      const busy = this.busyId === user.id;
      const modifiable = !user.isAdmin && user.id !== this.currentUserId;
      const blockLabel = user.isBlocked ? 'Débloquer' : 'Bloquer';
      const nextBlocked = !user.isBlocked;

      return `
        <tr class="${user.isBlocked ? 'admin-row-blocked' : ''}">
          <td>${esc(user.email) || '—'}</td>
          <td>
            ${esc(user.username)}
            ${user.isAdmin ? '<span class="admin-badge">Admin</span>' : ''}
            ${user.isGuest ? '<span class="admin-badge admin-badge-muted">Invité</span>' : ''}
          </td>
          <td>
            ${user.isBlocked ? '<span class="admin-badge admin-badge-danger">Bloqué</span>' : '<span class="admin-badge admin-badge-ok">Actif</span>'}
            ${user.isPremium ? '<span class="admin-badge admin-badge-ok">Orbit Pass</span>' : ''}
            ${!user.isGuest && !user.emailVerified ? '<span class="admin-badge admin-badge-muted">Email non vérifié</span>' : ''}
          </td>
          <td>${user.totalSessions ?? 0} / ${user.finishedSessions ?? 0}</td>
          <td>${user.totalScores ?? 0}</td>
          <td>${fmtDate(user.lastSeen)}</td>
          <td>
            <button type="button" class="btn btn--secondary admin-btn-sm"
              data-block-user="${user.id}" data-blocked="${nextBlocked}"
              ${!modifiable || busy ? 'disabled' : ''}>
              ${busy ? '…' : blockLabel}
            </button>
            ${user.isGuest ? '' : `<button type="button" class="btn btn--secondary admin-btn-sm"
              data-premium-user="${user.id}" data-premium="${user.isPremium ? 'false' : 'true'}"
              ${!modifiable || busy ? 'disabled' : ''}>
              ${user.isPremium ? '− Pass' : '+ Pass'}
            </button>`}
          </td>
        </tr>
      `;
    }).join('');
  }

  _render() {
    const g = this.guestStats;
    this.root.innerHTML = `
      <div class="admin-users">
        <section class="admin-panel-section admin-users-guest-section">
          <h2>Invités (aperçu)</h2>
          <div class="admin-stat-grid admin-users-guest-stats">
            <div class="admin-stat-card"><span class="admin-stat-value">${g.activeGuestAccounts ?? 0}</span><span class="admin-stat-label">Actifs</span></div>
            <div class="admin-stat-card"><span class="admin-stat-value">${g.newGuestAccounts7d ?? 0}</span><span class="admin-stat-label">Nouveaux 7j</span></div>
            <div class="admin-stat-card"><span class="admin-stat-value">${g.guestConversions7d ?? 0}</span><span class="admin-stat-label">Conversions 7j</span></div>
          </div>
        </section>

        <div class="admin-users-toolbar">
          <input type="search" class="admin-search" data-user-search placeholder="Rechercher pseudo ou email…" value="${esc(this.filter)}">
          <select data-account-filter class="admin-select" aria-label="Filtrer par type de compte">
            <option value="">Tous les comptes</option>
            <option value="guest"${this.accountFilter === 'guest' ? ' selected' : ''}>Invités</option>
            <option value="registered"${this.accountFilter === 'registered' ? ' selected' : ''}>Validés</option>
          </select>
        </div>

        <p class="admin-msg hidden" data-users-empty>Aucun utilisateur correspondant.</p>

        <div class="admin-table-scroll">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Pseudo</th>
                <th>Statut</th>
                <th>Parties (total / finies)</th>
                <th>Scores</th>
                <th>Dernière activité</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-users-tbody></tbody>
          </table>
        </div>
      </div>
    `;
    this._renderTable();
  }
}
